// UserService.js

import axios from "axios";

const BASE_CART_API_URL =
  process.env.REACT_APP_API_URL || "https://cgrmart.com/api";

const cartApi = axios.create({
  baseURL: BASE_CART_API_URL,
  timeout: 12000,
  headers: {
    "Content-Type": "application/json",
  },
});

const handleApiError = (error, defaultMessage) => {
  if (error?.response) {
    throw new Error(
      error.response.data?.message ||
        `${defaultMessage}. Server returned ${error.response.status}`
    );
  }
  if (error?.request) {
    throw new Error("Network error: Unable to reach the server");
  }
  throw new Error(error?.message || defaultMessage);
};

const requireUser = (user_identifier) => {
  if (!user_identifier) {
    throw new Error("User identifier required");
  }
  return String(user_identifier).trim();
};

const normalizeCartItem = (item) => {
  const totalPrice = Number(item.price ?? 0);
  const qty = Number.parseInt(item.quantity ?? 1, 10);
  const unitPrice = qty > 0 ? totalPrice / qty : totalPrice;

  // Extract product ID from various possible fields - check productid (lowercase) first
  const productId = 
    item.productid ||      // API returns lowercase productid
    item.product_id || 
    item.productId || 
    item.id;

  return {
    id: productId || `cart-${item.id || Date.now()}`,
    productId: productId, // Ensure productId is set for consistency
    name: item.product_name || item.name || "Product",
    supplier: item.supplier || item.companyname || "Supplier",
    image: item.image || item.imageurl || item.image_url,
    modelLabel: item.model_label || item.modelLabel || item.model || "",
    quantity: qty,
    unitPrice,
    cartItemId: item.id,
  };
};

/**
 * Some backends may return multiple rows for the same product in the cart
 * (e.g. duplicate inserts instead of an update). To avoid showing duplicate
 * lines in the UI, merge items that share the same logical product id.
 */
const mergeCartItemsByProduct = (items = []) => {
  const merged = [];
  const byKey = new Map();

  items.forEach((item) => {
    const key = item.productId || item.id;

    // If we cannot reliably identify the product, keep the row as‑is.
    if (!key) {
      merged.push(item);
      return;
    }

    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...item });
      return;
    }

    const totalQuantity = existing.quantity + item.quantity;
    const combinedValue =
      existing.unitPrice * existing.quantity + item.unitPrice * item.quantity;
    const mergedUnitPrice =
      totalQuantity > 0 ? combinedValue / totalQuantity : existing.unitPrice;

    byKey.set(key, {
      ...existing,
      quantity: totalQuantity,
      unitPrice: mergedUnitPrice,
    });
  });

  return [...merged, ...Array.from(byKey.values())];
};

export const fetchCart = async (user_identifier) => {
  try {
    const id = requireUser(user_identifier);
    const { data } = await cartApi.post("/check-cart", {
      user_identifier: id,
    });

    return {
      raw: data,
      items:
        data?.cart && Array.isArray(data.cart)
          ? mergeCartItemsByProduct(
              data.cart.map((item) => normalizeCartItem(item))
            )
          : [],
    };
  } catch (error) {
    handleApiError(error, "Failed to load cart");
  }
};

export const upsertCartItem = async ({
  user_identifier,
  product_id,
  product_name,
  quantity,
  price,
}) => {
  try {
    const id = requireUser(user_identifier);
    if (!product_id) throw new Error("Product ID required");
    if (!product_name) throw new Error("Product name required");
    if (quantity === undefined || quantity === null) throw new Error("Quantity required");
    if (price === undefined || price === null) throw new Error("Price required");

    // Validate and normalize data types
    const normalizedQuantity = Number.parseInt(quantity, 10);
    const normalizedPrice = Number.parseFloat(price);

    if (Number.isNaN(normalizedQuantity) || normalizedQuantity < 1) {
      throw new Error(`Invalid quantity: ${quantity}. Must be a positive integer.`);
    }
    if (Number.isNaN(normalizedPrice)) {
      throw new Error(`Invalid price: ${price}. Must be a valid number.`);
    }
    if (normalizedPrice <= 0) {
      throw new Error(`Invalid price: ${price}. Price must be greater than zero.`);
    }

    const payload = {
      user_identifier: id,
      product_id: String(product_id).trim(),
      product_name: String(product_name).trim(),
      quantity: normalizedQuantity,
      price: normalizedPrice,
    };

    const { data } = await cartApi.post("/add-to-cart", payload);

    return data;
  } catch (error) {
    // Log the full error for debugging
    console.error("upsertCartItem error:", {
      message: error.message,
      response: error?.response?.data,
      status: error?.response?.status,
      payload: {
        user_identifier,
        product_id,
        product_name,
        quantity,
        price,
      },
    });

    // If we have a response from the server, include it in the error message
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    // If it's a validation error we threw, re-throw it
    if (error.message && !error?.response) {
      throw error;
    }

    handleApiError(error, "Failed to add/update cart item");
  }
};

// Dedicated API for quantity-only updates (increment/decrement) using /update-to-cart
export const updateCartQuantity = async ({
  user_identifier,
  product_id,
  quantity,
  price,
}) => {
  try {
    const id = requireUser(user_identifier);
    if (!product_id) throw new Error("Product ID required");
    if (quantity === undefined) throw new Error("Quantity required");
    if (price === undefined) throw new Error("Price required");

    // DEBUG/FIX: Log the payload. This 'quantity' must be the absolute new count (e.g., 2, 3, 4).

    const { data } = await cartApi.post("/update-to-cart", {
      user_identifier: id,
      product_id,
      quantity,
      price,
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update cart quantity");
  }
};

export const removeCartItem = async (user_identifier, product_id) => {
  try {
    const id = requireUser(user_identifier);
    if (!product_id) throw new Error("Product ID required");

    const { data } = await cartApi.post("/remove-item", {
      user_identifier: id,
      product_id,
    });

    return data;
  } catch (error) {
    handleApiError(error, "Failed to remove item");
  }
};


// Legacy exports kept for backwards compatibility across pages
export const addToCart = upsertCartItem;
export const addOrUpdateCartItem = upsertCartItem;
export const updateCartItem = upsertCartItem;
export const checkCart = fetchCart;
export const removeItem = removeCartItem;



// Legacy placeholder retained
const API_URL = "https://example.com/api/users";
export const getUsers = () => cartApi.get(API_URL);